import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ComposedChart, BarChart, Bar, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Inbox, Target, Users, MousePointerClick, ArrowRight } from 'lucide-react';
import AnimatedCounter from '../../AnimatedCounter';
import { KpiCard, KpiCardData } from './KpiCards';
import { ChartCard, AXIS, GRID } from './DashboardCharts';
import { LeadsOverview, formatMonth } from './types';

const OUTCOME_COLORS = { vip: '#f59e0b', qualified: '#16a34a', nurture: '#93c5fd', crm: '#7c3aed' };
const QUIZ_STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  contacted: '#8b5cf6',
  converted: '#16a34a',
  lost: '#dc2626',
};
const CRM_STATUS_COLORS: Record<string, string> = {
  lead: '#3b82f6',
  negociacao: '#f59e0b',
  cliente_ativo: '#16a34a',
  inativo: '#9ca3af',
};

const percent = (part: number, total: number) => (total ? `${Math.round((part / total) * 100)}%` : '—');

interface SourceTableProps {
  title: string;
  columns: string[];
  rows: { key: string; cells: React.ReactNode[] }[];
}

const SourceTable: React.FC<SourceTableProps> = ({ title, columns, rows }) => {
  const { t } = useTranslation();
  return (
    <div className="min-w-0">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">{t('dashboardHome.leads.sources.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                {columns.map((col, i) => (
                  <th key={col} className={`py-2 font-medium ${i === 0 ? 'pr-3' : 'px-2 text-right'}`}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b border-gray-100 last:border-0">
                  {row.cells.map((cell, i) => (
                    <td
                      key={i}
                      className={i === 0 ? 'py-2 pr-3 text-gray-900 max-w-[12rem] truncate' : 'py-2 px-2 text-right tabular-nums text-gray-700'}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface LeadsSectionProps {
  leads: LeadsOverview;
}

const LeadsSection: React.FC<LeadsSectionProps> = ({ leads }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { kpis, charts, sources } = leads;

  const cards: KpiCardData[] = [
    {
      title: t('dashboardHome.leads.kpis.quizThisMonth'),
      value: <AnimatedCounter value={kpis.quizThisMonth} />,
      icon: Inbox,
      iconClass: 'bg-amber-500',
    },
    {
      title: t('dashboardHome.leads.kpis.quizConversion'),
      value: `${Math.round(kpis.quizConversionRate * 100)}%`,
      hint: t('dashboardHome.leads.kpis.quizConversionHint'),
      icon: Target,
      iconClass: 'bg-green-500',
    },
    {
      title: t('dashboardHome.leads.kpis.crmOpen'),
      value: <AnimatedCounter value={kpis.crmOpen} />,
      hint: t('dashboardHome.leads.kpis.crmNegotiation', { count: kpis.crmNegotiation }),
      icon: Users,
      iconClass: 'bg-violet-600',
    },
    {
      title: t('dashboardHome.leads.kpis.clicksThisMonth'),
      value: <AnimatedCounter value={kpis.clicksThisMonth} />,
      hint: t('dashboardHome.leads.kpis.clicksHint'),
      icon: MousePointerClick,
      iconClass: 'bg-blue-500',
    },
  ];

  const monthly = charts.monthly.map((row) => ({ ...row, label: formatMonth(row.month, lang) }));
  const quizFunnel = charts.quizFunnel.map((row) => ({ ...row, label: t(`dashboardHome.leads.quizStatus.${row.status}`) }));
  const crmFunnel = charts.crmFunnel.map((row) => ({ ...row, label: t(`dashboardHome.leads.crmStatus.${row.status}`) }));

  const funnelChart = (data: typeof quizFunnel, colors: Record<string, string>) => (
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
      <XAxis type="number" tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} />
      <YAxis type="category" dataKey="label" tick={AXIS} tickLine={false} axisLine={false} width={96} />
      <Tooltip cursor={{ fill: '#f3f4f6' }} />
      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
        {data.map((row) => (
          <Cell key={row.status} fill={colors[row.status] || '#9ca3af'} />
        ))}
      </Bar>
    </BarChart>
  );

  return (
    <section className="mt-6 md:mt-8">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('dashboardHome.leads.title')}</h2>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/admin/landing-pages/leads" className="inline-flex items-center gap-1 text-primary-600 hover:underline">
            {t('dashboardHome.leads.viewQuiz')} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/admin/crm/clientes" className="inline-flex items-center gap-1 text-primary-600 hover:underline">
            {t('dashboardHome.leads.viewCrm')} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {cards.map((card) => (
          <KpiCard key={card.title} {...card} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <ChartCard
          title={t('dashboardHome.leads.monthly')}
          empty={monthly.every((r) => !r.vip && !r.qualified && !r.nurture && !r.crm)}
        >
          <ComposedChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
            <Tooltip cursor={{ fill: '#f3f4f6' }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="vip" name={t('dashboardHome.leads.outcome.vip')} stackId="q" fill={OUTCOME_COLORS.vip} />
            <Bar dataKey="qualified" name={t('dashboardHome.leads.outcome.qualified')} stackId="q" fill={OUTCOME_COLORS.qualified} />
            <Bar
              dataKey="nurture"
              name={t('dashboardHome.leads.outcome.nurture')}
              stackId="q"
              fill={OUTCOME_COLORS.nurture}
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="crm"
              name={t('dashboardHome.leads.outcome.crm')}
              stroke={OUTCOME_COLORS.crm}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ChartCard>

        <ChartCard title={t('dashboardHome.leads.quizFunnel')} empty={quizFunnel.every((r) => !r.count)}>
          {funnelChart(quizFunnel, QUIZ_STATUS_COLORS)}
        </ChartCard>

        <ChartCard title={t('dashboardHome.leads.crmFunnel')} empty={crmFunnel.every((r) => !r.count)}>
          {funnelChart(crmFunnel, CRM_STATUS_COLORS)}
        </ChartCard>
      </div>

      <div className="card mt-6 md:mt-8">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 md:mb-4">{t('dashboardHome.leads.sources.title')}</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SourceTable
            title={t('dashboardHome.leads.sources.landings')}
            columns={[
              t('dashboardHome.leads.sources.landing'),
              t('dashboardHome.leads.sources.clicks'),
              t('dashboardHome.leads.sources.leads'),
              t('dashboardHome.leads.sources.converted'),
            ]}
            rows={sources.landings.map((row) => ({
              key: row.slug || '_none',
              cells: [
                row.slug ? `/${row.slug}` : t('dashboardHome.leads.sources.noLanding'),
                row.clicks,
                row.leads,
                percent(row.converted, row.leads),
              ],
            }))}
          />
          <SourceTable
            title={t('dashboardHome.leads.sources.campaigns')}
            columns={[
              t('dashboardHome.leads.sources.campaign'),
              t('dashboardHome.leads.sources.leads'),
              t('dashboardHome.leads.sources.converted'),
            ]}
            rows={sources.campaigns.map((row) => ({
              key: `${row.source}|${row.campaign}`,
              cells: [
                row.source || row.campaign
                  ? [row.source, row.campaign].filter(Boolean).join(' / ')
                  : t('dashboardHome.leads.sources.direct'),
                row.leads,
                percent(row.converted, row.leads),
              ],
            }))}
          />
          <SourceTable
            title={t('dashboardHome.leads.sources.crmOrigins')}
            columns={[t('dashboardHome.leads.sources.origin'), t('dashboardHome.leads.sources.count')]}
            rows={sources.crmOrigins.map((row) => ({
              key: row.origin || '_none',
              cells: [row.origin || t('dashboardHome.leads.sources.notInformed'), row.count],
            }))}
          />
        </div>
      </div>
    </section>
  );
};

export default LeadsSection;
